import { siteConfig } from "@acme/config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "~/components/legal/legal-page";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getPrivacyPolicyPageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/privacy-policy")({
  component: RouteComponent,
  head: () => {
    const seoData = seo({
      description: m.meta_privacy_description(),
      keywords:
        "Lindevo Privacy Policy, GDPR, Data Protection, Cookies, Analytics",
      noindex: true,
      path: "/privacy-policy",
      title: m.meta_privacy_title(),
    });
    const structuredData = generateStructuredDataGraph(
      getPrivacyPolicyPageSchemas()
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

/** Labels are localised; the ids are stable slugs other pages deep-link to. */
function getSections() {
  return [
    { id: "overview", title: m.privacy_section_1_title() },
    { id: "data-we-collect", title: m.privacy_section_2_title() },
    { id: "how-we-use", title: m.privacy_section_3_title() },
    { id: "cookies", title: m.privacy_section_4_title() },
    { id: "third-parties", title: m.privacy_section_5_title() },
    { id: "data-retention", title: m.privacy_section_6_title() },
    { id: "your-rights", title: m.privacy_section_7_title() },
    { id: "contact", title: m.privacy_section_8_title() },
  ];
}

/** One named processor and the policy it is governed by. */
function Processor({
  description,
  href,
  name,
}: Readonly<{ description: string; href: string; name: string }>) {
  return (
    <li>
      <strong>{name}</strong> — {description}{" "}
      {/* Five of these sit in one list; without the processor's name in the
          accessible name they all read as the same link. */}
      <a
        aria-label={`${name} — ${m.privacy_third_party_policy()}`}
        href={href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {m.privacy_third_party_policy()} <span aria-hidden="true">↗</span>
      </a>
    </li>
  );
}

function RouteComponent() {
  return (
    <LegalPage
      label={m.privacy_label()}
      lede={m.privacy_last_updated()}
      sections={getSections()}
      title={m.privacy_heading()}
    >
      <LegalSection id="overview" title={m.privacy_section_1_title()}>
        <p>{m.privacy_section_1_p1()}</p>
        <p>{m.privacy_section_1_commitment()}</p>
        <p>{m.privacy_section_1_p2()}</p>
        <p>{m.privacy_section_1_dpo()}</p>
        <p>
          <Link to="/legal">{m.privacy_section_1_legal_link()}</Link>
        </p>
      </LegalSection>

      <LegalSection id="data-we-collect" title={m.privacy_section_2_title()}>
        <p>{m.privacy_section_2_intro()}</p>
        <ul>
          <li>
            <strong>{m.privacy_section_2_item_contact()}</strong> —{" "}
            {m.privacy_section_2_item_contact_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_2_item_cookies()}</strong> —{" "}
            {m.privacy_section_2_item_cookies_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_2_item_analytics()}</strong> —{" "}
            {m.privacy_section_2_item_analytics_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_2_item_errors()}</strong> —{" "}
            {m.privacy_section_2_item_errors_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_2_item_chatbot()}</strong> —{" "}
            {m.privacy_section_2_item_chatbot_desc()}
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="how-we-use" title={m.privacy_section_3_title()}>
        <p>{m.privacy_section_3_intro()}</p>
        <ul>
          <li>
            <strong>{m.privacy_section_3_item_responding()}</strong> —{" "}
            {m.privacy_section_3_item_responding_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_3_item_improving()}</strong> —{" "}
            {m.privacy_section_3_item_improving_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_3_item_monitoring()}</strong> —{" "}
            {m.privacy_section_3_item_monitoring_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_3_item_ai()}</strong> —{" "}
            {m.privacy_section_3_item_ai_desc()}
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="cookies" title={m.privacy_section_4_title()}>
        <p>{m.privacy_section_4_intro()}</p>
        <ul>
          <li>
            <strong>{m.privacy_section_4_before_consent()}</strong> —{" "}
            {m.privacy_section_4_before_consent_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_4_accept()}</strong> —{" "}
            {m.privacy_section_4_accept_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_4_decline()}</strong> —{" "}
            {m.privacy_section_4_decline_desc()}
          </li>
        </ul>
        <p>{m.privacy_section_4_change()}</p>
      </LegalSection>

      <LegalSection id="third-parties" title={m.privacy_section_5_title()}>
        <p>{m.privacy_section_5_intro()}</p>
        <ul>
          <Processor
            description={m.privacy_section_5_google_desc()}
            href="https://policies.google.com/privacy"
            name={m.privacy_section_5_google()}
          />
          <Processor
            description={m.privacy_section_5_sentry_desc()}
            href="https://sentry.io/privacy/"
            name={m.privacy_section_5_sentry()}
          />
          <Processor
            description={m.privacy_section_5_openai_desc()}
            href="https://openai.com/privacy/"
            name={m.privacy_section_5_openai()}
          />
          <Processor
            description={m.privacy_section_5_resend_desc()}
            href="https://resend.com/legal/privacy-policy"
            name={m.privacy_section_5_resend()}
          />
          <Processor
            description={m.privacy_section_5_calendly_desc()}
            href="https://cal.com/privacy"
            name={m.privacy_section_5_calendly()}
          />
        </ul>
      </LegalSection>

      <LegalSection id="data-retention" title={m.privacy_section_6_title()}>
        <p>{m.privacy_section_6_intro()}</p>
        <ul>
          <li>
            <strong>{m.privacy_section_6_contact()}</strong> —{" "}
            {m.privacy_section_6_contact_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_6_analytics()}</strong> —{" "}
            {m.privacy_section_6_analytics_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_6_errors()}</strong> —{" "}
            {m.privacy_section_6_errors_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_6_chatbot()}</strong> —{" "}
            {m.privacy_section_6_chatbot_desc()}
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="your-rights" title={m.privacy_section_7_title()}>
        <p>{m.privacy_section_7_intro()}</p>
        <ul>
          <li>
            <strong>{m.privacy_section_7_access()}</strong> —{" "}
            {m.privacy_section_7_access_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_7_rectification()}</strong> —{" "}
            {m.privacy_section_7_rectification_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_7_erasure()}</strong> —{" "}
            {m.privacy_section_7_erasure_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_7_portability()}</strong> —{" "}
            {m.privacy_section_7_portability_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_7_object()}</strong> —{" "}
            {m.privacy_section_7_object_desc()}
          </li>
          <li>
            <strong>{m.privacy_section_7_withdraw()}</strong> —{" "}
            {m.privacy_section_7_withdraw_desc()}
          </li>
        </ul>
        <p>{m.privacy_section_7_exercise()}</p>
        <p>{m.privacy_section_7_complaint()}</p>
      </LegalSection>

      <LegalSection id="contact" title={m.privacy_section_8_title()}>
        <p>{m.privacy_section_8_intro()}</p>
        <p>
          <a href={`mailto:${siteConfig.contacts.mail}`}>
            {siteConfig.contacts.mail}
          </a>
        </p>
      </LegalSection>
    </LegalPage>
  );
}
